import { ref } from 'vue'
import { defineStore } from 'pinia'
import { LocalStorage } from 'quasar'

// TODO: Separate this when we have 2 projects
// Receiver
import type {
  CustomDataPingTestEvent,
  FunctionEvents,
  ImportCompletedEvent,
  MeEvent,
  NotificationEvent,
  NotificationRemovalEvent,
  SubscriptionEvent,
  TemplateDuplicationEvent,
} from 'types/receiver/account/events'
// Sender
import type {
  SourceEvent,
  TransformationEvent,
  ReportEvent,
  SubmitEvent,
  ColumnStatusEvent,
  StepsStatusEvent,
  PowerFunctionPopulateEvent,
  RefreshReportMetadataEvent,
} from 'types/sender/events/events'
// Common
import type {
  BannerHideEvent,
  BannerShowEvent,
  FileEvent,
} from 'types/common/events'

type EventTypes =
  // common
  | FileEvent
  | BannerHideEvent
  | BannerShowEvent
  // Receiver
  | NotificationEvent
  | NotificationRemovalEvent
  | FunctionEvents
  | ImportCompletedEvent
  | MeEvent
  | TemplateDuplicationEvent
  | SubscriptionEvent
  | CustomDataPingTestEvent
  // Sender
  | SourceEvent
  | TransformationEvent
  | ReportEvent
  | SubmitEvent
  | ColumnStatusEvent
  | StepsStatusEvent
  | PowerFunctionPopulateEvent
  | RefreshReportMetadataEvent
  | null

export const useEventsStore = defineStore('events', () => {
  const eventData = ref<EventTypes>(null)
  const eventIds = ref<string[]>([])
  const eventSource = ref<EventSource | null>(null)
  const eventStatus = ref<'OPEN' | 'CONNECTING' | 'CLOSED'>('CONNECTING')
  const eventError = ref<Event | null>(null)

  // reconnectFrequencySeconds doubles every retry
  const reconnectFrequencySeconds = ref(1)
  const timer = ref<null | NodeJS.Timeout>(null)

  const subscribeEvents = (channels: string[]) => {
    // Get the eventIds from local storage
    const ids = LocalStorage.getItem<string[]>('eventIds')
    if (ids) {
      eventIds.value = ids
    }

    const channelsStr = channels
      .map((el, i) => {
        let base = 'channels[]='
        if (i !== 0) {
          base = '&' + base
        }

        return `${base}${el}`
      })
      .join('')

    eventSource.value = new EventSource(
      `${process.env.API_BASE_URL}/events?${channelsStr}`
    )

    eventSource.value.onmessage = (msg) => {
      if (msg.data) {
        const data = { ...JSON.parse(msg.data) }

        if (!eventIds.value.includes(data.id)) {
          eventData.value = data

          eventIds.value.push(data.id)
          if (eventIds.value.length > 500) {
            eventIds.value.shift()
          }
        }
      }
    }

    eventSource.value.onopen = () => {
      eventStatus.value = 'OPEN'
    }

    eventSource.value.onerror = (error) => {
      error.preventDefault()
      eventStatus.value = 'CLOSED'
      eventError.value = error

      eventSource.value?.close()

      if (timer.value) {
        clearTimeout(timer.value)
      }
      // Reconnect
      timer.value = setTimeout(() => {
        eventSource.value = null
        subscribeEvents(channels)

        // Double every attempt
        reconnectFrequencySeconds.value *= 2
        // Max out at ~1 minute
        if (reconnectFrequencySeconds.value >= 64) {
          reconnectFrequencySeconds.value = 64
        }
      }, reconnectFrequencySeconds.value * 1000)
    }
  }

  const close = () => {
    // Save the eventIds to local storage
    LocalStorage.set('eventIds', eventIds.value)

    eventSource.value?.close()
    eventSource.value = null
    eventStatus.value = 'CLOSED'
  }

  // STORE RESET
  // Used to reset the store (is given by default by pinia on options api stores but not on composition api stores)
  const $reset = () => {
    eventSource.value = null
    eventStatus.value = 'CONNECTING'
    eventError.value = null

    reconnectFrequencySeconds.value = 1
    if (timer.value) {
      clearTimeout(timer.value)
      timer.value = null
    }
  }

  return {
    eventData,
    eventIds,
    eventStatus,
    eventSource,
    eventError,
    reconnectFrequencySeconds,
    timer,
    subscribeEvents,
    close,
    $reset,
  }
})
